import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Camera, Loader2, CheckCircle2, RefreshCcw } from "lucide-react";

/**
 * Scan a US driver's license / ID / passport via GPT-4o Vision and call onExtract(fields).
 * fields = { full_name, address, city, state, zip, id_number, dob, ... }
 */
export default function IDScanner({ agreementId, role, onExtract, scanFn }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setDone(false);
    // Preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    try {
      const result = await scanFn(agreementId, file);
      onExtract?.(result);
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not extract data. Try a clearer photo or fill manually.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`id-scanner-${role}`} className="csd-card p-5 bg-gradient-to-br from-white to-[var(--csd-primary-light)]/30">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--csd-primary)] grid place-items-center text-white shrink-0">
          <Camera size={18} />
        </div>
        <div className="flex-1">
          <div className="font-heading font-bold text-base">Scan your ID to autofill</div>
          <p className="text-sm text-[var(--csd-text-secondary)] mt-1 mb-3">
            Take a photo of your driver's license, state ID or passport. We'll extract your name, address and ID number.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="rounded-xl" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`id-scan-btn-${role}`}>
              {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Camera size={14} className="mr-2" />}
              {done ? "Scan another" : preview ? "Replace photo" : "Upload / Take photo"}
            </Button>
            {done && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--csd-primary-light)] text-[var(--csd-primary)]">
                <CheckCircle2 size={12} /> Data extracted — review fields below
              </span>
            )}
            {error && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                <RefreshCcw size={12} /> {error}
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--csd-text-secondary)] mt-2">JPG, PNG or WEBP. Max 8 MB. Always review extracted fields for accuracy.</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`id-scan-input-${role}`}
          />
          {preview && (
            <div className="mt-3">
              <img src={preview} alt="ID" className="max-h-32 rounded-lg border border-[var(--csd-border)] object-contain bg-gray-50" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
